import { forwardRef, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ChevronDown, ChevronRight, FileText, Plus, Pencil, Check, X, Trash2, History, ArrowUpDown } from 'lucide-react';
import { format } from 'date-fns';
import { ArtifactList } from '@/components/artifacts/ArtifactList';
import { ArtifactUploader } from '@/components/artifacts/ArtifactUploader';
import { ArtifactLinkCapture } from '@/components/artifacts/ArtifactLinkCapture';
import { LINK_ENTITY_TYPES, LINK_ROLES } from '@/lib/artifacts/constants';
import { fetchNoteRevisions, type NoteChainMember, type WorkflowNote } from '@/hooks/useWorkflowReport';
import {
  canModifyNote, canViewNoteHistory, formatNoteRoles, noteChainScopeLabel, noteOriginLabel, orderNotes, type NoteViewer,
} from '@/lib/notes/noteProvenance';

/** ≥44px on touch, compact with a mouse (same convention as TicketKebabMenu). */
const TOUCH = '[@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11';

interface WorkflowNotesSectionProps {
  /** Every visible note of the rollover chain, oldest first (FD-09). */
  notes: WorkflowNote[];
  /** The chain's days; one entry for a single-ticket repair. */
  chain?: NoteChainMember[];
  onAdd: (noteText: string) => void;
  /** FD-10: a change always carries a reason; the server keeps the previous text. */
  onUpdate: (id: string, noteText: string, reason: string, noteTicketId: string) => void;
  onDelete?: (id: string, reason: string, noteTicketId: string) => void;
  viewer?: NoteViewer;
  shopId?: string;
  /** Bumped by a deep link (ticket-card Notes shortcut): open the section so it can be brought into view. */
  focusRequest?: number;
}

const fmt = (iso: string) => format(new Date(iso), 'MMM d, h:mm a');

function NoteHistory({ noteId }: { noteId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['workflow-note-revisions', noteId],
    queryFn: () => fetchNoteRevisions(noteId),
  });
  if (isLoading) return <p className="text-xs text-muted-foreground">Loading history…</p>;
  if (isError) return <p className="text-xs text-destructive">Could not load the history.</p>;
  if (!data || data.length === 0) {
    return <p className="text-xs text-muted-foreground">No earlier versions are visible to you.</p>;
  }
  return (
    <ol className="space-y-2 border-l pl-3" data-testid="workflow-note-history">
      {data.map((r) => (
        <li key={r.id} className="text-xs space-y-0.5" data-testid="workflow-note-revision">
          <div className="text-muted-foreground">
            {r.action === 'delete' ? 'Deleted' : 'Edited'} by {r.editor_name || 'Unknown'}
            {formatNoteRoles(r.editor_roles) && ` (${formatNoteRoles(r.editor_roles)})`} • {fmt(r.created_at)}
          </div>
          <div className="italic">Reason: {r.reason}</div>
          <div className="whitespace-pre-wrap break-words">
            <span className="text-muted-foreground">Before: </span>{r.previous_text}
          </div>
        </li>
      ))}
    </ol>
  );
}

export const WorkflowNotesSection = forwardRef<HTMLDivElement, WorkflowNotesSectionProps>(function WorkflowNotesSection(
  { notes, chain = [], onAdd, onUpdate, onDelete, viewer = { userId: null, canCorrectOthers: false }, shopId, focusRequest },
  ref,
) {
  const [isOpen, setIsOpen] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editReason, setEditReason] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [newestFirst, setNewestFirst] = useState(false);

  useEffect(() => {
    if (focusRequest) setIsOpen(true);
  }, [focusRequest]);

  const chainLength = chain.length;
  const chainScope = noteChainScopeLabel(chain);
  const liveCount = notes.filter((n) => !n.deleted_at).length;
  const ordered = orderNotes(notes, newestFirst);

  const handleAdd = () => {
    if (!newText.trim()) return;
    onAdd(newText.trim());
    setNewText('');
    setIsAdding(false);
  };

  const handleUpdate = (note: WorkflowNote) => {
    if (!editText.trim() || !editReason.trim()) return;
    onUpdate(note.id, editText.trim(), editReason.trim(), note.ticket_id);
    setEditingId(null);
    setEditText('');
    setEditReason('');
  };

  const handleDelete = (note: WorkflowNote) => {
    if (!deleteReason.trim() || !onDelete) return;
    onDelete(note.id, deleteReason.trim(), note.ticket_id);
    setDeletingId(null);
    setDeleteReason('');
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} data-testid="workflow-notes-section" ref={ref} className="scroll-mt-2">
      <CollapsibleTrigger asChild>
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          tabIndex={0}
          data-testid="workflow-notes-header"
        >
          <div className="flex items-center gap-2 min-w-0">
            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            <FileText className="h-4 w-4 text-slate-500 shrink-0" />
            <span className="font-semibold text-sm">Internal Notes</span>
            <Badge variant="secondary" className="text-xs">{liveCount}</Badge>
            {chainScope && (
              <span className="text-xs text-muted-foreground truncate" data-testid="workflow-notes-chain-scope">
                {chainScope}
              </span>
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pt-2 pb-4 space-y-3">
          {notes.length > 1 && (
            <div className="flex justify-end">
              <Button
                size="sm" variant="ghost" className={`h-7 px-2 text-xs ${TOUCH}`}
                onClick={() => setNewestFirst((v) => !v)}
                data-testid="workflow-notes-order-toggle"
              >
                <ArrowUpDown className="h-3 w-3 mr-1" />{newestFirst ? 'Newest first' : 'Oldest first'}
              </Button>
            </div>
          )}

          <ol className="space-y-3" data-testid="workflow-notes-list">
            {ordered.map((n) => {
              const origin = noteOriginLabel(n.origin, chainLength, chain);
              const roles = formatNoteRoles(n.author_roles);
              const mayModify = canModifyNote(n, viewer);
              return (
                <li
                  key={n.id}
                  className={`rounded-lg p-3 space-y-1 ${n.deleted_at ? 'border border-dashed bg-muted/10' : 'bg-muted/30'}`}
                  data-testid="workflow-note"
                  data-note-id={n.id}
                  data-note-ticket={n.ticket_id}
                >
                  {editingId === n.id ? (
                    <div className="space-y-2">
                      <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} aria-label="Note text" />
                      <Input
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                        placeholder="Reason for the change (required)"
                        aria-label="Reason for the change"
                        data-testid="workflow-note-edit-reason"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => handleUpdate(n)} disabled={!editText.trim() || !editReason.trim()}>
                          <Check className="h-3 w-3 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditReason(''); }}>
                          <X className="h-3 w-3 mr-1" />Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {n.deleted_at ? (
                        <p className="text-sm italic text-muted-foreground" data-testid="workflow-note-deleted">
                          Note deleted by {n.deleter_name || 'Unknown'} • {fmt(n.deleted_at)}
                          {n.delete_reason ? ` — ${n.delete_reason}` : ''}
                        </p>
                      ) : (
                        <p className="text-sm whitespace-pre-wrap break-words">{n.note_text}</p>
                      )}
                      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <div className="min-w-0 space-y-0.5">
                          <div data-testid="workflow-note-provenance">
                            <span className="font-medium text-foreground/80">{n.author_name || 'Unknown'}</span>
                            {roles && <span data-testid="workflow-note-roles"> ({roles})</span>}
                            {' • '}{fmt(n.created_at)}
                            {origin && (
                              <Badge variant="outline" className="ml-2 text-[10px] py-0" data-testid="workflow-note-origin">{origin}</Badge>
                            )}
                          </div>
                          {!n.deleted_at && (n.edit_count ?? 0) > 0 && n.edited_at && (
                            <div data-testid="workflow-note-edited">
                              Edited by {n.editor_name || 'Unknown'}
                              {n.edited_by && n.edited_by !== n.created_by ? ' (not the author)' : ''} • {fmt(n.edited_at)}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {canViewNoteHistory(n, viewer) && (
                            <Button
                              size="sm" variant="ghost" className={`h-6 px-2 ${TOUCH}`}
                              aria-label="Show note history"
                              aria-expanded={historyId === n.id}
                              onClick={() => setHistoryId(historyId === n.id ? null : n.id)}
                              data-testid="workflow-note-history-toggle"
                            >
                              <History className="h-3 w-3" />
                            </Button>
                          )}
                          {mayModify && (
                            <>
                              <Button
                                size="sm" variant="ghost" className={`h-6 px-2 ${TOUCH}`} aria-label="Edit note"
                                data-testid="workflow-note-edit"
                                onClick={() => { setEditingId(n.id); setEditText(n.note_text); setEditReason(''); setDeletingId(null); }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              {onDelete && (
                                <Button
                                  size="sm" variant="ghost" className={`h-6 px-2 ${TOUCH}`} aria-label="Delete note"
                                  data-testid="workflow-note-delete"
                                  onClick={() => { setDeletingId(n.id); setDeleteReason(''); setEditingId(null); }}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {deletingId === n.id && (
                        <div className="space-y-2 pt-1" data-testid="workflow-note-delete-confirm">
                          <p className="text-xs">The note stays in the repair history; only its text is removed from the list.</p>
                          <Input
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder="Reason for deleting (required)"
                            aria-label="Reason for deleting"
                          />
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="destructive" onClick={() => handleDelete(n)} disabled={!deleteReason.trim()}>
                              Confirm delete
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setDeletingId(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {historyId === n.id && <NoteHistory noteId={n.id} />}

                      {/* Artifacts */}
                      {shopId && !n.deleted_at && (
                        <>
                          <ArtifactList
                            shopId={shopId}
                            entityType={LINK_ENTITY_TYPES.WORKFLOW_NOTE}
                            entityId={n.id}
                            canEdit={true}
                            className="mt-2"
                          />
                          <div className="flex items-center gap-1 mt-1">
                            <ArtifactUploader
                              shopId={shopId}
                              entityType={LINK_ENTITY_TYPES.WORKFLOW_NOTE}
                              entityId={n.id}
                              role={LINK_ROLES.NOTE_ATTACHMENT}
                            />
                            <ArtifactLinkCapture
                              shopId={shopId}
                              entityType={LINK_ENTITY_TYPES.WORKFLOW_NOTE}
                              entityId={n.id}
                              role={LINK_ROLES.NOTE_ATTACHMENT}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ol>

          {isAdding ? (
            <div className="space-y-2">
              <Textarea value={newText} onChange={e => setNewText(e.target.value)} placeholder="Add internal note..." rows={2} autoFocus />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAdd}><Check className="h-3 w-3 mr-1" />Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setIsAdding(false); setNewText(''); }}><X className="h-3 w-3 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setIsAdding(true)} data-testid="workflow-note-add">
              <Plus className="h-3 w-3 mr-1" />Add Note{chainLength > 1 ? ' to this ticket' : ''}
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});
